import { Fragment, useState } from 'react';
import {
  withStyles,
  WithStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';

import { breakpoints } from '../../../../Core/Theme';
import { trackGoogleBeamPromo } from '../../../../Core/Tracking';
import beamImage from './google-beam-promo.jpg';
import {
  GOOGLE_BEAM_CITIES,
  GOOGLE_BEAM_LANDING_URL,
  GOOGLE_BEAM_PROMO_STORAGE_KEY,
  getGoogleBeamBookingUrl,
} from './constants';

const COLORS = {
  teal: '#013E3F',
  tealHover: '#1F5556',
  cream: '#F9F7F3',
  title: '#0B1215',
  body: '#2B2A28',
  border: '#E6E0D4',
  closeBg: '#EFEBE3',
};

const MID_DESKTOP = `@media (min-width: ${breakpoints.MD}px) and (max-width: ${
  breakpoints.LG - 0.05
}px)`;

const readDismissed = () => {
  try {
    return localStorage.getItem(GOOGLE_BEAM_PROMO_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

type Props = WithStyles<typeof styles>;

const GoogleBeamPromo = ({ classes }: Props) => {
  const { t } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(readDismissed);

  if (isDismissed) return null;

  const onDismiss = () => {
    try {
      localStorage.setItem(GOOGLE_BEAM_PROMO_STORAGE_KEY, 'true');
    } catch {
      // Storage unavailable; hide for this session only.
    }
    trackGoogleBeamPromo('Dismiss');
    setIsDismissed(true);
  };

  const onExplore = () => {
    trackGoogleBeamPromo('Explore Google Beam');
  };

  return (
    <Grid item xs={12}>
      <section
        className={classes.card}
        aria-labelledby="google-beam-promo-title"
        data-testid="google-beam-promo"
      >
        <img
          src={beamImage}
          alt={t('home.google_beam_promo.image_alt')}
          className={classes.image}
        />

        <div className={classes.copy}>
          <span className={classes.eyebrow}>
            {t('home.google_beam_promo.eyebrow')}
          </span>
          <h2 id="google-beam-promo-title" className={classes.title}>
            {t('home.google_beam_promo.title')}
          </h2>
          <p className={classes.body}>{t('home.google_beam_promo.body')}</p>
          <p className={classes.cities} data-testid="google-beam-promo-cities">
            <svg
              className={classes.pin}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden="true"
            >
              <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21Z" />
              <circle cx="12" cy="9.5" r="2.5" />
            </svg>
            <span>
              {GOOGLE_BEAM_CITIES.map(({ city, location, locationId }, i) => (
                <Fragment key={city}>
                  {i > 0 && (
                    <span className={classes.separator} aria-hidden="true">
                      {' · '}
                    </span>
                  )}
                  <a
                    href={getGoogleBeamBookingUrl(locationId)}
                    className={classes.cityLink}
                    aria-label={t('home.google_beam_promo.city_link', {
                      city,
                      location,
                    })}
                    onClick={() => trackGoogleBeamPromo(`City: ${city}`)}
                  >
                    {city}
                  </a>
                </Fragment>
              ))}
            </span>
          </p>
        </div>

        <div className={classes.actions}>
          <a
            href={GOOGLE_BEAM_LANDING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.cta}
            onClick={onExplore}
            data-testid="google-beam-promo-cta"
          >
            {t('home.google_beam_promo.cta')}
          </a>
        </div>

        <button
          type="button"
          className={classes.closeButton}
          onClick={onDismiss}
          aria-label={t('home.google_beam_promo.close')}
          data-testid="google-beam-promo-close-btn"
        >
          <CloseIcon className={classes.closeIcon} />
        </button>
      </section>
    </Grid>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    card: {
      position: 'relative',
      // With the homepage grid spacing (24px desktop, 8px mobile) this
      // leaves a 40px gap below the reservation cards at every width.
      marginTop: 16,
      display: 'grid',
      gridTemplateColumns: 'minmax(240px, min(32%, 420px)) minmax(0, 1fr) auto',
      alignItems: 'center',
      background: '#FFFFFF',
      border: `1px solid ${COLORS.border}`,
      borderRadius: 24,
      color: COLORS.title,
      // Small desktops: the button moves under the copy so the text keeps
      // enough width.
      [MID_DESKTOP]: {
        gridTemplateColumns: 'minmax(240px, min(32%, 420px)) minmax(0, 1fr)',
        alignItems: 'start',
      },
      [theme.breakpoints.down(breakpoints.MD)]: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      },
      [theme.breakpoints.down(breakpoints.MOBILE)]: {
        marginTop: 32,
      },
    },
    image: {
      display: 'block',
      width: '100%',
      height: '100%',
      minHeight: 220,
      objectFit: 'cover',
      objectPosition: '55% 45%',
      // Ignore the photo's own size so the copy, not the image, sets the
      // card's height on wide screens.
      contain: 'size',
      borderRadius: '23px 0 0 23px',
      alignSelf: 'stretch',
      [MID_DESKTOP]: {
        gridRow: '1 / 3',
      },
      [theme.breakpoints.down(breakpoints.MD)]: {
        height: 240,
        minHeight: 0,
        borderRadius: '23px 23px 0 0',
      },
      [theme.breakpoints.down(breakpoints.MOBILE)]: {
        height: 200,
      },
    },
    copy: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      // String units: this JSS version does not add px to numeric gap.
      gap: '10px',
      padding: '24px 40px',
      [MID_DESKTOP]: {
        padding: '32px 64px 0 40px',
      },
      [theme.breakpoints.down(breakpoints.MD)]: {
        padding: '28px 32px 0',
      },
      [theme.breakpoints.down(breakpoints.MOBILE)]: {
        padding: '24px 20px 0',
      },
    },
    eyebrow: {
      fontFamily: 'VerlagBold',
      fontSize: 13,
      letterSpacing: '0.12em',
      color: COLORS.teal,
      textTransform: 'uppercase',
    },
    title: {
      margin: 0,
      fontFamily: 'TiemposHeadline, Georgia, serif',
      fontWeight: 500,
      fontSize: 34,
      lineHeight: 1.15,
      letterSpacing: '-0.015em',
      color: COLORS.title,
      textWrap: 'pretty',
      [theme.breakpoints.down(breakpoints.MD)]: {
        fontSize: 28,
      },
      [theme.breakpoints.down(breakpoints.MOBILE)]: {
        fontSize: 24,
      },
    },
    body: {
      margin: 0,
      maxWidth: 560,
      fontFamily: 'VerlagBook',
      fontSize: 17,
      lineHeight: 1.5,
      color: COLORS.body,
      textWrap: 'pretty',
      [theme.breakpoints.down(breakpoints.MOBILE)]: {
        fontSize: 16,
      },
    },
    cities: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '6px',
      margin: '4px 0 0',
      fontFamily: 'VerlagBold',
      fontSize: 16,
      lineHeight: 1.4,
      color: COLORS.teal,
    },
    pin: {
      flex: 'none',
      marginTop: 2,
    },
    separator: {
      color: COLORS.teal,
      margin: '0 4px',
    },
    cityLink: {
      color: COLORS.teal,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      '&:hover': {
        color: COLORS.tealHover,
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
      },
      '&:focus-visible': {
        outline: 'none',
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
      },
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      // Leaves room for the close button in the top-right corner.
      margin: '0 56px 0 16px',
      [MID_DESKTOP]: {
        margin: 0,
        padding: '20px 40px 32px',
      },
      [theme.breakpoints.down(breakpoints.MD)]: {
        margin: 0,
        padding: '24px 32px 32px',
      },
      [theme.breakpoints.down(breakpoints.MOBILE)]: {
        padding: '20px 20px 24px',
      },
    },
    cta: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 56,
      minWidth: 220,
      padding: '0 32px',
      borderRadius: 12,
      background: COLORS.teal,
      color: COLORS.cream,
      fontFamily: 'VerlagBold',
      fontSize: 18,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      transition: 'background 150ms ease-out, transform 150ms ease-out',
      '&:hover': {
        background: COLORS.tealHover,
        color: COLORS.cream,
      },
      '&:active': {
        transform: 'scale(0.98)',
      },
      '&:focus-visible': {
        outline: 'none',
        boxShadow: `0 0 0 3px rgba(1, 62, 63, 0.35)`,
      },
      [theme.breakpoints.down(breakpoints.MOBILE)]: {
        width: '100%',
        minWidth: 0,
        padding: '0 20px',
      },
    },
    closeButton: {
      position: 'absolute',
      top: 16,
      right: 16,
      width: 36,
      height: 36,
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      borderRadius: '50%',
      background: COLORS.closeBg,
      cursor: 'pointer',
      '&:hover': {
        background: COLORS.border,
      },
      '&:focus-visible': {
        outline: 'none',
        boxShadow: `0 0 0 3px rgba(1, 62, 63, 0.35)`,
      },
      [theme.breakpoints.down(breakpoints.MD)]: {
        // Sits on top of the photo once the card stacks.
        background: 'rgba(255, 255, 255, 0.9)',
      },
    },
    closeIcon: {
      width: 18,
      height: 18,
      color: COLORS.title,
    },
  });

export default withStyles(styles)(GoogleBeamPromo);
